import { useSelector } from 'react-redux';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { fetcherToPlainText } from '../../../../../shared/fetchers';
import { FOSSABOT_SUPPORTED_FETCHERS } from '../../../../../shared/fossabot/common';
import { DependencyUsage, dependencyUsageFromDepth } from '../../../../../shared/issuesV2/common';
import { isProjectScope } from '../../../../../shared/issuesV2/helpers';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';
import type { Fetcher } from '../../../../../shared/fetchers';

import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotFixBox } from './FossabotFixBox';
import { FossabotNoFixBox } from './FossabotNoFixBox';
import { FossabotOtherFixesBox } from './FossabotOtherFixesBox';
import { FossabotProjectScopePrompt } from './FossabotProjectScopePrompt';
import { useFossabotConnection } from './useFossabotConnection';
import { useFossabotNoFix } from './useFossabotNoFix';
import { useFossabotPrFlow } from './useFossabotPrFlow';
import type { FossabotBlockedNotice } from './FossabotFixBox';
import type { FossabotPrFlow } from './useFossabotPrFlow';

/** Why Create PR is disabled when the repository is not connected; the banner's Connect button is the CTA. */
const NOT_CONNECTED_REASON = 'Connect fossabot to create PRs';

/** Why Create PR is not offered on a transitive dependency: fossabot only edits the manifest. */
const TRANSITIVE_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for transitive dependencies',
  description: 'This package is pulled in by another dependency, not declared directly in your project. To fix this, upgrade the direct dependency that includes it.',
};

/*
  TODO(CORE-7662): the copy on the four notices below is placeholder, waiting
  on design sign-off.
*/

/** Why Create PR is not offered on a CLI-uploaded project: there is no connected repository to open a PR on. */
const CLI_PROJECT_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for CLI projects',
  description: 'This project was uploaded with the FOSSA CLI, so fossabot has no connected repository to open a PR on.',
};

/** Why Create PR is not offered on a snippet match: the code was copied in, not declared in a manifest. */
const SNIPPET_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for snippets',
  description: 'This code was matched as a snippet, not declared in a manifest, so fossabot has nothing to upgrade.',
};

/** Why Create PR is not offered on a vendored dependency: same as a snippet, there is no manifest entry to bump. */
const VENDORED_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for vendored dependencies',
  description: 'This dependency is checked in to your code, not declared in a manifest, so fossabot has nothing to upgrade.',
};

/** Why Create PR is not offered on a manually added dependency: it was added in FOSSA, so no manifest declares it. */
const MANUAL_NOTICE: FossabotBlockedNotice = {
  heading: 'PRs cannot be opened for manually added dependencies',
  description: 'This dependency was manually added in FOSSA, not declared in a manifest, so fossabot has nothing to upgrade.',
};

/** Vendored dependencies are uploaded by the CLI as archives. */
const VENDORED_FETCHER = 'archive';

/** Manually added dependencies get the user fetcher. */
const MANUAL_FETCHER = 'user';

/** Why Create PR is not offered for a dependency whose ecosystem fossabot does not support. */
const getUnsupportedEcosystemNotice = (fetcher: string): FossabotBlockedNotice => {
  const ecosystem = fetcherToPlainText(fetcher as Fetcher) ?? fetcher;
  return {
    heading: `${ecosystem} dependencies are not supported`,
    description: `fossabot cannot open PRs for ${ecosystem} packages. Review the supported ecosystems documentation below.`,
    hasEcosystemsLink: true,
  };
};

/** The fix cards still render when not connected, in their idle state with Create PR disabled. */
const NOT_CONNECTED_FLOW: FossabotPrFlow = {
  status: 'none',
  vcsUrl: null,
  fossabotUrl: null,
  onCreatePR: () => undefined,
};

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
type Props = {
  /** Switches the drawer to the Projects tab, where each affected project links to project scope. */
  onViewProjects: () => void;
};

export const FossabotRemediationPanel: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const { mutate: startInstall } = useStartGitHubAppInstall();
  const projectLocator = isProjectScope(scope) ? scope.id : '';
  const connection = useFossabotConnection(projectLocator);

  /*
    fossabot upgrades a dependency by editing the manifest, so it can only reach
    direct dependencies. Transitive ones fail in the fossabot service after the
    click, so Create PR is not offered up front instead. A project we cannot find
    in the issue, or one with an unknown depth, fails open and stays clickable.
    The same goes for an ecosystem fossabot does not support; an issue with no
    package manager fails open too.
  */
  const project = isProjectScope(scope) ? issue?.projects.find(({ id }) => id === scope.id) : null;
  const isTransitive = dependencyUsageFromDepth(project?.depth ?? null) === DependencyUsage.Deep;
  const fetcher = issue?.source?.packageManager;

  /*
    These cases have nothing to connect or set up, so the notice shows without
    the connection banner, whatever the connection state.
  */
  const getNoSetupNotice = (): FossabotBlockedNotice | undefined => {
    if (projectLocator.startsWith('custom+')) {
      return CLI_PROJECT_NOTICE;
    }
    if (issue?.analysisSnippetId) {
      return SNIPPET_NOTICE;
    }
    if (fetcher === MANUAL_FETCHER) {
      return MANUAL_NOTICE;
    }
    return fetcher === VENDORED_FETCHER ? VENDORED_NOTICE : undefined;
  };
  const noSetupNotice = getNoSetupNotice();

  const getBlockedNotice = (): FossabotBlockedNotice | undefined => {
    if (noSetupNotice) {
      return noSetupNotice;
    }
    if (fetcher && !FOSSABOT_SUPPORTED_FETCHERS.has(fetcher)) {
      return getUnsupportedEcosystemNotice(fetcher);
    }
    return isTransitive ? TRANSITIVE_NOTICE : undefined;
  };
  const blockedNotice = getBlockedNotice();

  const isConnected = connection.state === 'connected';
  const isLoading = connection.state === 'checking';
  const noFixType = useFossabotNoFix();
  const prFlow = useFossabotPrFlow(isConnected);

  /*
    While the connection status is loading, render no box; it resolves in a
    moment and the banner already covers that state. With no fix to offer,
    neither box has anything to show, so both give way to the unfixable state,
    unless a no-setup notice explains why fossabot cannot help here at all.
    Not connected, or the status check failed and we cannot tell: the same fix
    cards, with Create PR disabled and the banner's Connect button as the CTA.
  */
  const renderBox = (): ReactNode => {
    if (isLoading && !noSetupNotice) {
      return null;
    }
    if (noFixType !== null && !noSetupNotice) {
      return <FossabotNoFixBox type={noFixType} />;
    }

    const flow = isConnected ? prFlow : NOT_CONNECTED_FLOW;
    if (!flow) {
      return null;
    }

    const disabledReason = isConnected ? undefined : NOT_CONNECTED_REASON;

    return (
      <>
        <FossabotFixBox
          status={flow.status}
          vcsUrl={flow.vcsUrl}
          fossabotUrl={flow.fossabotUrl}
          onCreatePR={flow.onCreatePR}
          errorMessage={flow.errorMessage}
          onRetry={flow.onRetry}
          disabledReason={disabledReason}
          blockedNotice={blockedNotice}
        />
        {/* The other fixes are all fossabot PRs, so there is nothing to offer when PRs are blocked. */}
        {!blockedNotice && <FossabotOtherFixesBox onCreatePR={flow.onCreatePR} disabledReason={disabledReason} />}
      </>
    );
  };

  /*
    Matches the sibling sections (Remediation, Header): with no issue in Redux
    there is nothing to remediate, so render nothing rather than an empty card.
  */
  if (!issue) {
    return null;
  }

  /*
    Outside project scope (global / release group) a fossabot PR cannot be
    created (it targets exactly one repository), so the connection banner and
    fix boxes give way to a prompt pointing the user at project scope, regardless
    of connection state.
  */
  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          {isProjectScope(scope) ? (
            <>
              {!blockedNotice && (
                <FossabotConnectionBanner
                  state={connection.state}
                  errorMessage={getErrorMessage(connection.error, 'Could not check fossabot connection status.')}
                  onConnect={() => startInstall('fossabot')}
                />
              )}
              {renderBox()}
            </>
          ) : (
            <FossabotProjectScopePrompt onViewProjects={onViewProjects} />
          )}
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
